import React from "react";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import Collapse from "@material-ui/core/Collapse";
import { NavLink } from "react-router-dom";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { hasSidebarAccess } from "./sidebarMenuConfig";

const ActiveIndicator = () => null;

const cx = (...classNames) => classNames.filter(Boolean).join(" ");

const slugify = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const getItemKey = (item, parentKey, index) => {
  if (item.id) {
    return item.id;
  }

  const base = slugify(item.label) || slugify(item.to) || `item-${index}`;
  return parentKey ? `${parentKey}/${base}` : base;
};

const SidebarIcon = ({ classes, icon, iconClassName, menuIconClass }) => (
  <ListItemIcon className={classes.menuItemIcon}>
    <i className={cx(icon, menuIconClass, iconClassName)}></i>
  </ListItemIcon>
);

const SidebarChildren = ({ item, parentKey, renderItems }) => {
  const children = renderItems(item.children, parentKey);

  if (item.wrapChildrenInList) {
    return (
      <List component="div" disablePadding>
        {children}
      </List>
    );
  }

  return children;
};

const SidebarLinkItem = ({
  item,
  t,
  classes,
  isProd,
  location,
  menuIconClass,
  onCloseSidebar,
  onPermissionSelect,
}) => {
  const isActive = location.pathname === item.to;
  const activeClassName = isActive ? (isProd ? "active-ghmc" : "active-swmnew") : "";

  const handleItemClick = (event) => {
    if (item.permissionId) {
      onPermissionSelect(item.permissionId);
    }
    onCloseSidebar(event);
  };

  return (
    <ListItem
      onClick={handleItemClick}
      button
      className={cx(classes.menuItem, activeClassName)}
      component={NavLink}
      to={item.to}
      title={t(item.label)}
      style={item.style}
    >
      <SidebarIcon
        classes={classes}
        icon={item.icon}
        iconClassName={item.iconClassName}
        menuIconClass={menuIconClass}
      />
      <ListItemText
        inset={item.textInset}
        primary={t(item.label)}
        style={item.textStyle}
      />
      {isActive && <ActiveIndicator />}
    </ListItem>
  );
};

const SidebarSectionItem = ({
  item,
  sectionId,
  parentKey,
  siblingSectionIds,
  t,
  classes,
  isProd,
  location,
  menuIconClass,
  openSections,
  onToggleSection,
  onCloseSidebar,
  onPermissionSelect,
  renderItems,
}) => {
  const isOpen = Boolean(openSections[sectionId]);
  const openClassName = isOpen ? (isProd ? "bg-ghmc" : "bg-swm") : "bg-transparent";
  const activeClassName = item.activeWhen?.(location.pathname)
    ? cx(isProd ? "active-ghmc" : "active-swmnew", "icon-white")
    : "";
  const sectionClassName =
    item.itemClassName === ""
      ? cx(activeClassName, openClassName)
      : cx(classes.menuItem, activeClassName, openClassName, item.itemClassName);

  return (
    <>
      <ListItem
        button
        onClick={() =>
          onToggleSection(sectionId, { siblingSectionIds, parentKey })
        }
        className={sectionClassName}
        title={t(item.label)}
        style={item.style}
      >
        <SidebarIcon
          classes={classes}
          icon={item.icon}
          iconClassName={item.iconClassName}
          menuIconClass={menuIconClass}
        />
        <ListItemText
          inset={item.textInset}
          primary={t(item.label)}
          style={item.textStyle}
        />
        {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </ListItem>
      <Collapse
        in={isOpen}
        timeout="auto"
        className={item.collapseClassName}
        unmountOnExit
      >
        {item.showDividerBeforeChildren && <Divider />}
        <SidebarChildren
          item={item}
          parentKey={sectionId}
          renderItems={renderItems}
        />
      </Collapse>
    </>
  );
};

export const SidebarMenuItems = ({
  items,
  t,
  classes,
  isProd,
  location,
  menuIconClass,
  permissions,
  openSections,
  onToggleSection,
  onCloseSidebar,
  onPermissionSelect,
}) => {
  const renderItems = (menuItems = [], parentKey = "") => {
    const hasAccessibleChildren = (menuItem) => {
      if (!menuItem.children || menuItem.children.length === 0) {
        return true;
      }
      return menuItem.children.some((child) => {
        if (child.enabled === false) return false;
        
        if (child.type === "section") {
          return hasAccessibleChildren(child);
        } else {
          return hasSidebarAccess(child.access, permissions);
        }
      });
    };

    const siblingSectionIds = menuItems
      .map((item, index) => ({ item, index }))
      .filter(
        ({ item }) =>
          item.enabled !== false &&
          item.type === "section" &&
          hasAccessibleChildren(item)
      )
      .map(({ item, index }) => getItemKey(item, parentKey, index));

    return menuItems.map((item, index) => {
      if (item.enabled === false) {
        return null;
      }

      if (item.type === "section") {
        if (!hasAccessibleChildren(item)) {
          return null;
        }
      } else {
        if (!hasSidebarAccess(item.access, permissions)) {
          return null;
        }
      }

      const itemKey = getItemKey(item, parentKey, index);

      if (item.type === "section") {
        return (
          <SidebarSectionItem
            key={itemKey}
            sectionId={itemKey}
            parentKey={parentKey}
            siblingSectionIds={siblingSectionIds.filter((id) => id !== itemKey)}
            item={item}
            t={t}
            classes={classes}
            isProd={isProd}
            location={location}
            menuIconClass={menuIconClass}
            openSections={openSections}
            onToggleSection={onToggleSection}
            onCloseSidebar={onCloseSidebar}
            onPermissionSelect={onPermissionSelect}
            renderItems={renderItems}
          />
        );
      }

      return (
        <SidebarLinkItem
          key={itemKey}
          item={item}
          t={t}
          classes={classes}
          isProd={isProd}
          location={location}
          menuIconClass={menuIconClass}
          onCloseSidebar={onCloseSidebar}
          onPermissionSelect={onPermissionSelect}
        />
      );
    });
  };

  return renderItems(items);
};
